/**
 * The 44px column at the end of a template row.
 *
 * Was two ghost icon buttons (a pencil and a bin) sitting inside a `<tr>` whose
 * own click opened the editor, which meant every press had to call
 * `stopPropagation` to avoid doing two things at once.
 *
 * A built-in template can still be edited - that is how an account adapts the
 * wording it ships with - so the only thing `builtin` changes here is the
 * delete entry, which the API refuses anyway.
 */
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { ContractTemplate } from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";

export function TemplateRowMenu({
  template,
  onEdit,
  onDelete,
}: {
  template: ContractTemplate;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const t = useT();
  const close = useClosePops();

  return (
    // Above the row-covering overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            onEdit();
          }}
        >
          <Pencil className="size-4" />
          {t("templates.edit")}
        </button>

        {template.builtin ? null : (
          <>
            <div className="crm-menu-sep" />
            <button
              type="button"
              className="crm-mi crm-mi-danger"
              onClick={() => {
                close();
                onDelete();
              }}
            >
              <Trash2 className="size-4" />
              {t("common.delete")}
            </button>
          </>
        )}
      </Pop>
    </span>
  );
}
